import { ITEMS, STACK_LIMIT, STACK_DEFAULT, type ItemId } from '../game/inventory';
import { ASSETS, PLAYER, MOVE, UPKEEP, DAYNIGHT } from '../config';
import { rollAssetLoot } from '../game/assetField';
import { RECIPES, type Recipe } from './crafting';
import { RESEARCH, DEFAULT_RESEARCH_COST } from '../game/research';
import { persistRuleset, resetToDefaults } from '../ruleset';
import { TH, el, tBtn, tNum, gridBg } from './theme';
import { assetThumb } from './assetThumb';
import { WEAPONS, AMMO_META, AMMO_STATS, WEAPON_IDS, type WeaponId } from '../game/weapons';
import { ITEM_ICONS } from './iconManifest';
import { ObjectLab, type LabMode } from './objectLab';
import { renderGunLab, GUN_IDS } from './gunLab';
import { WEAPONS as WEAPON_TABLE } from '../game/weapons';

// THE /admin control panel — moody dark, sidebar IA, schema-seeded forms + grid editors. Multi-tenant-ready shell.
// Opens on F4 or #gmadmin. Saves persist across reloads (config-as-data).

type Section = 'research' | 'recipes' | 'loot' | 'stacks' | 'weapons' | 'world' | 'players' | 'server' | 'objects_buildables' | 'objects_icons' | 'objects_guns';
const NAV: Array<{ group: string; items: Array<[Section, string]> }> = [
  { group: 'Game Data', items: [['research', 'Research tree'], ['recipes', 'Recipes'], ['loot', 'Loot tables'], ['stacks', 'Stack limits'], ['weapons', 'Weapons & damage']] },
  { group: 'World', items: [['world', 'Player & survival']] },
  { group: 'Objects', items: [['objects_buildables', 'Buildables'], ['objects_icons', 'Icons'], ['objects_guns', 'Guns']] }, // GLB / icon / per-gun fit calibration
  { group: 'Players', items: [['players', 'Players & roles']] },
  { group: 'Server', items: [['server', 'Settings & logs']] },
];
const RAIL = new Set<Section>(['research', 'objects_buildables', 'objects_icons', 'objects_guns']); // these use the left palette as a picker (drag-to-add for research, click-to-select for objects)
const CRAFTABLE = new Set<string>(RECIPES.map((r) => r.id));
const GRID = 120;
const snap = (v: number): number => Math.round(v / GRID) * GRID;
const ASSET_ICON: Record<string, string> = { barrel: '◍', crate: '▣', toolbox: '▤', junk: '◆' };
const assetGlyph = (id: string): string => ASSET_ICON[Object.keys(ASSET_ICON).find((k) => id.toLowerCase().startsWith(k)) ?? ''] ?? '⬡';

// Schema seed for the World section (idea #5 start): each field declares its block + range; the form renders itself.
const FIELD: Array<{ cat: string; block: Record<string, number>; key: string; label: string; min: number; max: number; hint?: string }> = [
  { cat: 'Player', block: PLAYER as unknown as Record<string, number>, key: 'maxHealth', label: 'Max health', min: 25, max: 500 },
  { cat: 'Player', block: PLAYER as unknown as Record<string, number>, key: 'bleedOutSeconds', label: 'Bleed-out time', min: 5, max: 120, hint: 'seconds' },
  { cat: 'Movement', block: MOVE as unknown as Record<string, number>, key: 'walkSpeed', label: 'Walk speed', min: 1, max: 15 },
  { cat: 'Movement', block: MOVE as unknown as Record<string, number>, key: 'sprintSpeed', label: 'Sprint speed', min: 1, max: 25 },
  { cat: 'Movement', block: MOVE as unknown as Record<string, number>, key: 'crouchSpeed', label: 'Crouch speed', min: 0.5, max: 10 },
  { cat: 'Movement', block: MOVE as unknown as Record<string, number>, key: 'jumpVelocity', label: 'Jump power', min: 2, max: 20 },
  { cat: 'Movement', block: MOVE as unknown as Record<string, number>, key: 'gravity', label: 'Gravity', min: 5, max: 40 },
  { cat: 'Economy', block: UPKEEP as unknown as Record<string, number>, key: 'ratePerDay', label: 'Upkeep per day', min: 0, max: 1, hint: '0.2 = 20%' },
  { cat: 'Day / Night', block: DAYNIGHT as unknown as Record<string, number>, key: 'daySeconds', label: 'Day length', min: 30, max: 3600, hint: 'seconds (full day; transitions are dusk/dawn)' },
  { cat: 'Day / Night', block: DAYNIGHT as unknown as Record<string, number>, key: 'duskSeconds', label: 'Dusk length', min: 10, max: 900, hint: 'seconds' },
  { cat: 'Day / Night', block: DAYNIGHT as unknown as Record<string, number>, key: 'nightSeconds', label: 'Night length', min: 30, max: 3600, hint: 'seconds' },
  { cat: 'Day / Night', block: DAYNIGHT as unknown as Record<string, number>, key: 'dawnSeconds', label: 'Dawn length', min: 10, max: 900, hint: 'seconds' },
];

export class AdminPage {
  private readonly root: HTMLElement;
  private readonly content: HTMLElement;
  private readonly railWrap: HTMLElement;
  private readonly crumb: HTMLElement;
  private readonly saveState: HTMLElement;
  private readonly navEls = new Map<Section, HTMLElement>();
  private palette!: HTMLElement;
  private search!: HTMLInputElement;
  private section: Section = 'research';
  private dirty = false;
  private resSel: string[] = [];
  private resTier = 0;                  // research tab: 0 = Default list, 1/2/3 = workbench-tier tree
  private researchRefresh: (() => void) | null = null;
  private pan = { x: 60, y: 60 };
  private zoom = 1;
  private toastT = 0;
  private objectLab: ObjectLab | null = null;
  private selectedObjKey: string | null = null;
  open = false;
  onVisibility: ((open: boolean) => void) | null = null; // main wires this to suppress game input while the panel is up

  constructor() {
    this.root = el('div', `position:fixed;inset:0;z-index:200;display:none;background:${TH.bg};color:${TH.text};font:${TH.f3} system-ui,sans-serif`);
    // a faint vignette for depth/mood
    this.root.append(el('div', `position:absolute;inset:0;pointer-events:none;background:radial-gradient(120% 80% at 50% -10%, rgba(201,119,46,0.05), transparent 55%), radial-gradient(100% 100% at 50% 120%, rgba(0,0,0,0.5), transparent 60%)`));

    const shell = el('div', 'display:flex;height:100%;position:relative');

    // ---- sidebar ----
    const side = el('div', `width:240px;flex:none;background:${TH.side};border-right:1px solid ${TH.line};display:flex;flex-direction:column`);
    const brand = el('div', `padding:${TH.s5} ${TH.s5} ${TH.s4}`);
    brand.append(el('div', `font:800 ${TH.f5} system-ui;letter-spacing:4px;color:${TH.rustHi}`, 'OXIDE'),
      el('div', `font:600 ${TH.f1} system-ui;letter-spacing:2px;color:${TH.dim};margin-top:2px`, 'CONTROL PANEL'));
    // tenant switcher (placeholder — multi-tenant header, idea #9)
    const tenant = el('div', `margin:0 ${TH.s4} ${TH.s4};padding:${TH.s2} ${TH.s3};background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.rsm};display:flex;align-items:center;gap:${TH.s2};cursor:pointer`);
    tenant.append(el('div', `width:7px;height:7px;border-radius:50%;background:${TH.good};box-shadow:0 0 6px ${TH.good}`), el('div', `flex:1;font:600 ${TH.f2} system-ui;color:${TH.text}`, 'Survival · Server 1'), el('div', `color:${TH.dim}`, '▾'));
    tenant.title = 'Server switcher (multi-tenant) — coming in M7';
    const nav = el('div', 'flex:1;overflow:auto;padding:0 ' + TH.s3);
    for (const g of NAV) {
      nav.append(el('div', `padding:${TH.s4} ${TH.s2} ${TH.s1};font:700 ${TH.f1} system-ui;letter-spacing:1.5px;color:${TH.faint}`, g.group.toUpperCase()));
      for (const [id, label] of g.items) {
        const it = el('div', `display:flex;align-items:center;gap:${TH.s2};padding:${TH.s2} ${TH.s3};margin:1px 0;border-radius:${TH.rsm};cursor:pointer;color:${TH.dim};font:${TH.f3} system-ui;border-left:2px solid transparent`, label);
        it.addEventListener('click', () => this.go(id));
        it.addEventListener('mouseenter', () => { if (this.section !== id) it.style.background = TH.panel; });
        it.addEventListener('mouseleave', () => { if (this.section !== id) it.style.background = 'transparent'; });
        this.navEls.set(id, it);
        nav.append(it);
      }
    }
    side.append(brand, tenant, nav);

    // ---- main ----
    const main = el('div', 'flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0');
    const top = el('div', `display:flex;align-items:center;gap:${TH.s4};padding:0 ${TH.s5};height:54px;flex:none;border-bottom:1px solid ${TH.line};background:linear-gradient(180deg,${TH.bg2},${TH.bg})`);
    this.crumb = el('div', `font:600 ${TH.f3} system-ui;color:${TH.dim}`);
    this.saveState = el('div', `font:${TH.f2} system-ui;color:${TH.dim}`, 'Saved');
    top.append(this.crumb, spacer(), this.saveState,
      tBtn('Save', () => { persistRuleset(); this.setDirty(false); this.toast('Saved — survives reload'); }, 'primary'),
      tBtn('Reset', () => { resetToDefaults(); localStorage.removeItem('oxide.configPatch'); this.setDirty(false); this.renderMain(); this.refillPalette(); this.toast('Reset to defaults'); }, 'danger'),
      tBtn('✕', () => this.hide()));

    const body = el('div', 'flex:1;display:flex;overflow:hidden;min-height:0');
    // item rail (conditional)
    this.railWrap = el('div', `width:230px;flex:none;border-right:1px solid ${TH.line};background:${TH.bg2};display:flex;flex-direction:column`);
    this.search = el('input', `margin:${TH.s3};padding:7px 10px;background:${TH.sunk};border:1px solid ${TH.line};border-radius:${TH.rsm};color:${TH.text};font:${TH.f3} system-ui;box-shadow:${TH.inset}`);
    this.search.placeholder = 'Search items…';
    this.search.addEventListener('input', () => this.refillPalette());
    this.palette = el('div', `flex:1;overflow:auto;padding:0 ${TH.s3} ${TH.s3};display:flex;flex-direction:column;gap:3px`);
    this.railWrap.append(el('div', `padding:${TH.s3} ${TH.s3} 0;font:700 ${TH.f1} system-ui;letter-spacing:1px;color:${TH.faint}`, 'ITEMS — DRAG ONTO A SYSTEM'), this.search, this.palette);
    this.content = el('div', `flex:1;overflow:auto;padding:${TH.s5};min-width:0`);
    body.append(this.railWrap, this.content);

    main.append(top, body);
    shell.append(side, main);
    this.root.append(shell);
    document.body.appendChild(this.root);
  }

  private go(s: Section): void { this.section = s; this.paintNav(); this.refillPalette(); this.renderMain(); }
  private paintNav(): void {
    for (const [id, it] of this.navEls) {
      const on = id === this.section;
      it.style.background = on ? TH.glow : 'transparent';
      it.style.color = on ? TH.text : TH.dim;
      it.style.borderLeftColor = on ? TH.rust : 'transparent';
      it.style.fontWeight = on ? '600' : '400';
    }
  }
  private setDirty(v: boolean): void { this.dirty = v; this.saveState.textContent = v ? '● Unsaved changes' : 'Saved'; this.saveState.style.color = v ? TH.warn : TH.dim; }
  private markDirty(): void { this.setDirty(true); }

  private renderMain(): void {
    this.objectLab?.unmount(); // leaving whatever section we were in -- a harmless no-op if it wasn't mounted
    this.railWrap.style.display = RAIL.has(this.section) ? 'flex' : 'none';
    const g = NAV.find((x) => x.items.some(([id]) => id === this.section))!;
    const label = g.items.find(([id]) => id === this.section)![1];
    this.crumb.textContent = `${g.group}  ›  ${label}`;
    this.content.replaceChildren();
    if (this.section === 'research') this.renderResearch();
    else if (this.section === 'recipes') this.renderRecipes();
    else if (this.section === 'loot') this.renderLoot();
    else if (this.section === 'stacks') this.renderStacks();
    else if (this.section === 'weapons') this.renderWeapons();
    else if (this.section === 'world') this.renderWorld();
    else if (this.section === 'objects_buildables') this.renderObjects('buildables');
    else if (this.section === 'objects_icons') this.renderObjects('icons');
    else if (this.section === 'objects_guns') this.renderGuns();
    else this.renderPlaceholder(label);
  }

  // ---- OBJECTS: GLB / icon-sprite scale calibration (F4 owner-only tool, ui/objectLab.ts) ----
  private renderObjects(mode: LabMode): void {
    this.content.append(heading(mode === 'buildables' ? 'Buildables' : 'Icons',
      mode === 'buildables'
        ? 'Every .glb under public/models/, tested against a real stone foundation + wall + half-wall + ceiling + a player-height body. Pick one on the left, dial in SCALE, then Save.'
        : 'Every item icon, shown as the exact floating ground-drop sprite the game uses. Pick one on the left, dial in SCALE, then Save.'));
    if (!this.objectLab) this.objectLab = new ObjectLab();
    this.objectLab.mount(this.content, mode);
  }

  // ---- OBJECTS -> GUNS: per-gun FP/ADS/muzzle/3P fit (ui/gunLab.ts; live on the equipped weapon) ----
  private selectedGun = 'pistol';
  private renderGuns(): void {
    this.content.append(heading('Guns',
      'Per-gun fit: FP seat, ADS offset, MUZZLE point (flash + tracers) and 3P seat. TIP: press F8 IN-GAME instead — same sliders as a side overlay, with view locks (1st / 1st ADS / 3rd / 3rd AIM) so you see the gun while you drag. Save writes weapon_fit.json.'));
    const host = el('div', '');
    this.content.append(host);
    renderGunLab(host, this.selectedGun, () => this.markDirty());
  }

  private renderPlaceholder(label: string): void {
    const box = el('div', `display:flex;flex-direction:column;align-items:center;justify-content:center;height:70%;gap:${TH.s3};color:${TH.faint}`);
    box.append(el('div', `font:${TH.f6} system-ui`, '◴'), el('div', `font:600 ${TH.f4} system-ui;color:${TH.dim}`, label), el('div', `font:${TH.f3} system-ui`, 'Coming with the server + multiplayer milestones.'));
    this.content.append(box);
  }

  // ---- World: schema-seeded scalar form (table) ----
  private renderWorld(): void {
    this.content.append(heading('Player & survival', 'Core tunables — applies live, Save to persist. New fields here render from a schema, no hand-coding.'));
    const table = el('div', `border:1px solid ${TH.line};border-radius:${TH.r};overflow:hidden;max-width:620px;background:${TH.panel}`);
    let cat = '';
    for (const f of FIELD) {
      if (f.cat !== cat) { cat = f.cat; table.append(el('div', `padding:${TH.s2} ${TH.s4};background:${TH.bg2};color:${TH.dim};font:700 ${TH.f1} system-ui;letter-spacing:1.5px;border-top:1px solid ${TH.line}`, cat.toUpperCase())); }
      const row = el('div', `display:flex;align-items:center;gap:${TH.s4};padding:9px ${TH.s4};border-top:1px solid ${TH.lineSoft}`);
      row.append(el('div', `flex:1;color:${TH.text};font:${TH.f3} system-ui`, f.label),
        el('div', `color:${TH.faint};font:${TH.f1} system-ui`, f.hint ?? `${f.min}–${f.max}`),
        tNum(f.block[f.key], (v) => { f.block[f.key] = v; this.markDirty(); }, { min: f.min, title: `${f.min}–${f.max}` }));
      table.append(row);
    }
    this.content.append(table);
  }

  // ---- palette (context-aware) ----
  private refillPalette(): void {
    if (!RAIL.has(this.section)) return;
    this.palette.replaceChildren();
    if (this.section === 'objects_buildables' || this.section === 'objects_icons') { this.fillObjectsPalette(); return; }
    if (this.section === 'objects_guns') { this.fillGunsPalette(); return; }
    const q = this.search.value.trim().toLowerCase();
    const research = this.section === 'research';
    const hint = research ? el('div', `padding:2px 4px 6px;font:${TH.f1} system-ui;color:${TH.faint}`, 'Unassigned craftables. Click to add to the open tab.') : null;
    if (hint) this.palette.append(hint);
    for (const [id, def] of Object.entries(ITEMS)) {
      if (research) { if (!CRAFTABLE.has(id) || RESEARCH.defaults.includes(id) || RESEARCH.nodes[id]) continue; } // only UNASSIGNED craftables (defaults/placed are removed)
      if (q && !def.name.toLowerCase().includes(q) && !id.toLowerCase().includes(q)) continue;
      const chip = el('div', `display:flex;align-items:center;gap:${TH.s2};padding:6px 9px;background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.rsm};cursor:${research ? 'pointer' : 'grab'};user-select:none`);
      chip.draggable = true;
      chip.addEventListener('dragstart', (e) => e.dataTransfer?.setData('text/plain', id));
      chip.addEventListener('mouseenter', () => { chip.style.background = TH.hover; });
      chip.addEventListener('mouseleave', () => { chip.style.background = TH.panel; });
      chip.append(swatch(def.color), el('span', `flex:1;color:${TH.text};font:${TH.f3} system-ui`, def.name));
      if (research) {
        chip.addEventListener('click', (e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') this.addToResearch(id); }); // click body → add to the open tab
        const ci = tNum(RESEARCH.costs[id] ?? DEFAULT_RESEARCH_COST, (v) => { RESEARCH.costs[id] = Math.max(0, Math.floor(v)); this.markDirty(); }, { min: 0, w: 48, title: 'Salvage to research (carries onto the tree)' });
        ci.addEventListener('mousedown', (e) => e.stopPropagation()); // type without starting a drag
        chip.append(ci);
      }
      this.palette.append(chip);
    }
  }

  // Guns palette: every gun/bow id -- click to select it into the fit editor.
  private fillGunsPalette(): void {
    this.search.placeholder = 'Search guns…';
    const q = this.search.value.trim().toLowerCase();
    for (const id of GUN_IDS) {
      const name = id === 'ak' ? 'AK' : (WEAPON_TABLE[id as keyof typeof WEAPON_TABLE]?.name ?? id);
      if (q && !name.toLowerCase().includes(q) && !id.includes(q)) continue;
      const on = this.selectedGun === id;
      const chip = el('div', `flex-shrink:0;padding:8px 10px;background:${on ? TH.hover : TH.panel};border:1px solid ${on ? TH.rust : TH.line};border-radius:${TH.rsm};cursor:pointer;user-select:none;color:${TH.text};font:${TH.f3} system-ui`, name);
      chip.addEventListener('click', () => { this.selectedGun = id; this.refillPalette(); this.renderMain(); });
      this.palette.append(chip);
    }
  }

  // Objects palette: buildable .glb filenames (fetched from the dev-only /__list_models endpoint) or every
  // item icon id. Click-to-select (no drag) -- loads it into the ObjectLab viewport for resize/move/save.
  private fillObjectsPalette(): void {
    if (!this.objectLab) this.objectLab = new ObjectLab();
    const lab = this.objectLab;
    this.search.placeholder = this.section === 'objects_buildables' ? 'Search .glb files…' : 'Search items…';
    const q = this.search.value.trim().toLowerCase();
    const pickRow = (key: string, label: string): HTMLElement => {
      const on = this.selectedObjKey === key;
      // flex-shrink:0 is load-bearing: this.palette is a flex COLUMN with 100+ children in the icons list,
      // and a plain block div with no nested span (unlike the item-palette chips elsewhere in this file)
      // was getting flex-compressed down to ~14px tall -- shorter than one line of 13px text needs once
      // padding is subtracted, so overflow:hidden clipped every label down to a sliver of stray pixels.
      const chip = el('div', `flex:none;padding:6px 9px;background:${on ? TH.glow : TH.panel};border:1px solid ${on ? TH.rust : TH.line};border-radius:${TH.rsm};cursor:pointer;color:${TH.text};font:${TH.f3} system-ui;line-height:1.3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap`, label);
      chip.addEventListener('mouseenter', () => { if (this.selectedObjKey !== key) chip.style.background = TH.hover; });
      chip.addEventListener('mouseleave', () => { if (this.selectedObjKey !== key) chip.style.background = TH.panel; });
      chip.addEventListener('click', () => { this.selectedObjKey = key; void lab.select(key); this.refillPalette(); });
      return chip;
    };
    if (this.section === 'objects_buildables') {
      this.palette.append(el('div', `padding:2px 4px 6px;font:${TH.f1} system-ui;color:${TH.faint}`, 'Loading public/models/…'));
      void lab.listModels().then((files) => {
        this.palette.replaceChildren();
        for (const f of files) { if (q && !f.toLowerCase().includes(q)) continue; this.palette.append(pickRow(f, f)); }
        if (!files.length) this.palette.append(el('div', `padding:8px;color:${TH.faint};font:${TH.f2} system-ui`, 'No .glb files in public/models/ (or the dev endpoint is not loaded -- restart the dev server after pulling).'));
      });
    } else {
      for (const id of ITEM_ICONS) {
        const name = ITEMS[id as ItemId]?.name ?? id;
        if (q && !name.toLowerCase().includes(q) && !id.toLowerCase().includes(q)) continue;
        this.palette.append(pickRow(id, name));
      }
    }
  }

  // Add a palette item to whatever research tab is open: the Default list (tier 0) or a node at the current tier.
  private addToResearch(id: string): void {
    if (this.resTier === 0) { if (!RESEARCH.defaults.includes(id)) RESEARCH.defaults.push(id); }
    else if (!RESEARCH.nodes[id]) {
      const n = Object.values(RESEARCH.nodes).filter((m) => m.tier === this.resTier).length;
      RESEARCH.nodes[id] = { reqs: [], tier: this.resTier, x: snap((this.resTier - 1) * 500 + (n % 3) * 235 + 40), y: snap(Math.floor(n / 3) * 132 + 40) }; // cost lives in RESEARCH.costs[id]
    }
    this.markDirty(); this.refillPalette(); this.researchRefresh?.();
  }

  // ---- stack limits (table) ----
  private renderStacks(): void {
    this.content.append(heading('Stack limits', 'Click “Add item” to cap an item’s stack. Unlisted items use the default (' + STACK_DEFAULT + ').'));
    const wrap = el('div', 'max-width:560px');
    const table = el('div', `border:1px solid ${TH.line};border-radius:${TH.r};overflow:hidden;background:${TH.panel}`);
    const body = el('div', '');
    const add = (id: string): void => {
      if ([...body.children].some((c) => (c as HTMLElement).dataset.id === id)) return;
      const row = el('div', `display:flex;align-items:center;gap:${TH.s3};padding:8px ${TH.s4};border-top:1px solid ${TH.lineSoft}`); row.dataset.id = id;
      row.append(swatch(ITEMS[id as ItemId]?.color ?? 0x888888), el('div', `flex:1;color:${TH.text};font:${TH.f3} system-ui`, ITEMS[id as ItemId]?.name ?? id),
        tNum(STACK_LIMIT[id as ItemId] ?? STACK_DEFAULT, (v) => { STACK_LIMIT[id as ItemId] = Math.max(1, Math.floor(v)); this.markDirty(); }, { min: 1, title: 'Max stack' }),
        iconBtn('✕', () => { delete STACK_LIMIT[id as ItemId]; row.remove(); this.markDirty(); }));
      STACK_LIMIT[id as ItemId] = STACK_LIMIT[id as ItemId] ?? STACK_DEFAULT;
      body.append(row);
    };
    table.append(el('div', `display:flex;padding:7px ${TH.s4};background:${TH.bg2};font:700 ${TH.f1} system-ui;letter-spacing:1px;color:${TH.dim}`, undefined));
    const hdr = el('div', `display:flex;align-items:center;padding:7px ${TH.s4};background:${TH.bg2};font:700 ${TH.f1} system-ui;letter-spacing:1px;color:${TH.dim}`);
    hdr.append(el('div', 'width:21px', ''), el('div', 'flex:1', 'ITEM'), el('div', '', 'MAX STACK'));
    table.replaceChildren(hdr, body);
    wrap.append(table, this.addSlot(add));
    this.content.append(wrap);
    for (const id of Object.keys(STACK_LIMIT)) add(id);
  }

  // ---- recipes (grouped grid of cards) ----
  private renderRecipes(): void {
    this.content.append(heading('Recipes', 'Ingredients + craft time per recipe. The workbench tier needed to craft is set on the Research tree.'));
    const groups = new Map<string, Recipe[]>();
    for (const r of RECIPES) { const c = recipeCat(r.id); if (!groups.has(c)) groups.set(c, []); groups.get(c)!.push(r); }
    for (const [cat, list] of groups) {
      this.content.append(sectionLabel(cat));
      const grid = cardGrid(236);
      for (const r of list) grid.append(this.recipeCard(r));
      this.content.append(grid);
    }
  }

  private recipeCard(r: Recipe): HTMLElement {
    const card = el('div', cardCss);
    const head = el('div', `display:flex;align-items:center;gap:${TH.s2}`);
    head.append(swatch(ITEMS[r.id]?.color ?? 0x888888), el('span', `flex:1;font:700 ${TH.f3} system-ui;color:${TH.text}`, ITEMS[r.id]?.name ?? r.id));
    const metaEl = el('div', `font:${TH.f1} system-ui;color:${TH.dim};margin-top:3px`);
    const meta = (): void => { metaEl.textContent = `${r.cost.length} ingredient${r.cost.length === 1 ? '' : 's'}`; }; // workbench tier is set in Research now, not here
    const ings = el('div', `display:flex;flex-direction:column;gap:4px;margin-top:${TH.s2}`);
    const draw = (): void => {
      ings.replaceChildren();
      for (const c of r.cost) {
        const row = el('div', `display:flex;align-items:center;gap:6px;background:${TH.sunk};border:1px solid ${TH.lineSoft};border-radius:${TH.rsm};padding:4px 7px`);
        row.append(swatch(ITEMS[c.item]?.color ?? 0x888888), el('span', `flex:1;font:${TH.f2} system-ui;color:${TH.text}`, ITEMS[c.item]?.name ?? c.item),
          tNum(c.amount, (v) => { c.amount = Math.max(1, Math.floor(v)); this.markDirty(); }, { min: 1, w: 56 }),
          iconBtn('✕', () => { r.cost = r.cost.filter((x) => x !== c); draw(); meta(); this.markDirty(); }));
        ings.append(row);
      }
      if (!r.cost.length) ings.append(el('div', `font:${TH.f1} system-ui;color:${TH.faint};padding:3px 0`, 'No ingredients yet.'));
    };
    const add = (id: string): void => { if (!r.cost.some((c) => c.item === id)) { r.cost.push({ item: id as ItemId, amount: 1 }); draw(); meta(); this.markDirty(); } };
    draw(); meta();
    const timeRow = el('div', `display:flex;align-items:center;gap:6px;margin-top:${TH.s2};padding-top:${TH.s2};border-top:1px solid ${TH.lineSoft}`);
    timeRow.append(el('span', `flex:1;font:${TH.f2} system-ui;color:${TH.dim}`, 'Craft time'),
      tNum(r.seconds ?? 0, (v) => { r.seconds = Math.max(0, v); this.markDirty(); }, { min: 0, w: 54, title: 'seconds to craft' }),
      el('span', `font:${TH.f1} system-ui;color:${TH.faint}`, 'sec'));
    card.replaceChildren(head, metaEl, ings, this.addSlot(add), timeRow);
    return card;
  }

  // ---- loot tables (grouped asset cards with 3D thumbnails) ----
  private renderLoot(): void {
    this.content.append(heading('Loot tables', 'Click “Add item” to add loot. MIN–MAX rolled, CHANCE % to drop at all.'));
    const groups = new Map<string, string[]>();
    for (const aid of Object.keys(ASSETS.defs)) { const c = assetCat(aid); if (!groups.has(c)) groups.set(c, []); groups.get(c)!.push(aid); }
    for (const [cat, list] of groups) {
      this.content.append(sectionLabel(cat));
      const grid = cardGrid(300);
      for (const aid of list) grid.append(this.lootCard(aid));
      this.content.append(grid);
    }
  }

  private lootCard(aid: string): HTMLElement {
    const def = ASSETS.defs[aid];
    const card = el('div', cardCss);
    const thumb = el('div', `width:44px;height:44px;border-radius:7px;background:${TH.sunk};border:1px solid ${TH.line};background-size:cover;background-position:center;flex:none`);
    const url = assetThumb(aid); if (url) thumb.style.backgroundImage = `url(${url})`; else thumb.textContent = assetGlyph(aid);
    const titleWrap = el('div', 'flex:1;min-width:0');
    const metaEl = el('div', `font:${TH.f1} system-ui;color:${TH.dim}`);
    titleWrap.append(el('div', `font:700 ${TH.f3} system-ui;color:${TH.text}`, prettify(aid)), metaEl);
    const head = el('div', `display:flex;align-items:center;gap:${TH.s2}`); head.append(thumb, titleWrap);
    const cols = '1fr 44px 44px 50px 22px';
    const hdr = el('div', `display:grid;grid-template-columns:${cols};gap:6px;font:700 ${TH.f1} system-ui;color:${TH.faint};letter-spacing:0.5px;padding:0 2px;margin-top:${TH.s2}`);
    hdr.append(el('span', '', 'ITEM'), right('MIN'), right('MAX'), right('%'), el('span', '', ''));
    const rows = el('div', 'display:flex;flex-direction:column;gap:5px;margin-top:3px');
    const meta = (): void => { metaEl.textContent = `${def.loot.length} drop${def.loot.length === 1 ? '' : 's'}  ·  ${def.method === 'open' ? 'open (E)' : def.hits + ' hits'}`; };
    const draw = (): void => {
      rows.replaceChildren();
      for (const lr of def.loot) {
        const pct = Math.round((lr[3] ?? 1) * 100);
        const name = el('div', 'min-width:0');
        name.append(el('div', `font:${TH.f2} system-ui;color:${TH.text};overflow:hidden;text-overflow:ellipsis;white-space:nowrap`, ITEMS[lr[0] as ItemId]?.name ?? lr[0]));
        const fill = el('div', `height:100%;border-radius:2px;width:${pct}%;background:${pct >= 80 ? TH.good : pct >= 40 ? TH.warn : TH.bad}`);
        const bar = el('div', `height:2px;border-radius:2px;margin-top:2px;background:${TH.line}`); bar.append(fill); name.append(bar);
        const row = el('div', `display:grid;grid-template-columns:${cols};gap:6px;align-items:center`);
        row.append(name,
          tNum(lr[1], (v) => { lr[1] = Math.max(0, Math.floor(v)); this.markDirty(); }, { min: 0, w: 42 }),
          tNum(lr[2], (v) => { lr[2] = Math.max(0, Math.floor(v)); this.markDirty(); }, { min: 0, w: 42 }),
          tNum(pct, (v) => { const p = Math.max(0, Math.min(100, v)); lr[3] = p / 100; fill.style.width = p + '%'; fill.style.background = p >= 80 ? TH.good : p >= 40 ? TH.warn : TH.bad; this.markDirty(); }, { min: 0, w: 48, title: '% chance to drop' }),
          iconBtn('✕', () => { def.loot = def.loot.filter((x) => x !== lr); draw(); meta(); this.markDirty(); }));
        rows.append(row);
      }
    };
    const add = (id: string): void => { if (!def.loot.some((l) => l[0] === id)) { def.loot.push([id, 1, 2, 1]); draw(); meta(); this.markDirty(); } };
    draw(); meta();
    const sim = el('div', `font:${TH.f1} ui-monospace,monospace;color:${TH.dim};margin-top:${TH.s2};min-height:13px`);
    const simBtn = tBtn('🎲 Simulate ×1000', () => {
      const tally: Record<string, number> = {};
      for (let i = 0; i < 1000; i++) for (const d of rollAssetLoot(aid as never)) tally[d.id] = (tally[d.id] ?? 0) + d.amount;
      sim.textContent = Object.entries(tally).map(([k, v]) => `${ITEMS[k as ItemId]?.name ?? k} ${(v / 1000).toFixed(1)}`).join('  ·  ') || 'nothing';
    }); simBtn.style.cssText += `;padding:5px 10px;font-size:${TH.f1};margin-top:${TH.s2}`;
    card.replaceChildren(head, hdr, rows, this.addSlot(add), simBtn, sim);
    return card;
  }

  // ---- weapons & damage (grouped cards, one per weapon; ammo modifiers as a small table below) ----
  private renderWeapons(): void {
    this.content.append(heading('Weapons & damage', 'Damage, rate of fire, and building/blast damage per weapon. Explosive ammo modifiers are below the gun list.'));
    const groups = new Map<string, WeaponId[]>();
    for (const id of WEAPON_IDS) { const c = weaponCat(id); if (!groups.has(c)) groups.set(c, []); groups.get(c)!.push(id); }
    const order = ['Melee', 'Bows', 'Guns', 'Throwables & explosives'];
    for (const cat of order) {
      const list = groups.get(cat); if (!list) continue;
      this.content.append(sectionLabel(cat));
      const grid = cardGrid(260);
      for (const id of list) grid.append(this.weaponCard(id));
      this.content.append(grid);
    }
    this.content.append(sectionLabel('Ammo damage modifiers'), this.ammoModTable());
  }

  private weaponCard(id: WeaponId): HTMLElement {
    const def = WEAPONS[id];
    const card = el('div', cardCss);
    const head = el('div', `display:flex;align-items:center;gap:${TH.s2}`);
    head.append(swatch(def.color), el('span', `flex:1;font:700 ${TH.f3} system-ui;color:${TH.text}`, def.name),
      el('span', `font:${TH.f1} system-ui;color:${TH.faint};text-transform:uppercase;letter-spacing:0.5px`, def.kind));
    const rows = el('div', `display:flex;flex-direction:column;gap:5px;margin-top:${TH.s3}`);
    const stat = (label: string, value: number, onChange: (v: number) => void, opts?: { min?: number; hint?: string }): void => {
      const row = el('div', `display:flex;align-items:center;gap:${TH.s2}`);
      row.append(el('span', `flex:1;font:${TH.f2} system-ui;color:${TH.dim}`, label),
        tNum(value, (v) => { onChange(v); this.markDirty(); }, { min: opts?.min ?? 0, w: 60 }),
        el('span', `font:${TH.f1} system-ui;color:${TH.faint};width:34px`, opts?.hint ?? ''));
      rows.append(row);
    };
    stat('Damage' + (def.pellets ? ' / pellet' : ''), def.damage, (v) => { def.damage = v; });
    if (def.pellets != null) stat('Pellets', def.pellets, (v) => { def.pellets = Math.max(1, Math.floor(v)); });
    if (def.rpm != null) stat('Rate of fire', def.rpm, (v) => { def.rpm = Math.max(1, v); }, { hint: 'rpm' });
    if (def.spreadDeg != null) stat('Hip spread', def.spreadDeg, (v) => { def.spreadDeg = v; }, { hint: 'deg' });
    if (def.recoil != null) stat('Recoil', def.recoil, (v) => { def.recoil = v; });
    if (def.range != null) stat('Range', def.range, (v) => { def.range = v; }, { hint: 'm' });
    if (def.mag != null) stat('Magazine', def.mag, (v) => { def.mag = Math.max(1, Math.floor(v)); });
    if (def.reloadTime != null) stat('Reload', def.reloadTime, (v) => { def.reloadTime = v; }, { hint: 'sec' });
    if (def.cooldown != null) stat('Cooldown', def.cooldown, (v) => { def.cooldown = v; }, { hint: 'sec' });
    if (def.drawTime != null) stat('Draw time', def.drawTime, (v) => { def.drawTime = v; }, { hint: 'sec' });
    if (def.explosive) {
      const ex = def.explosive;
      rows.append(el('div', `font:700 ${TH.f1} system-ui;color:${TH.faint};letter-spacing:1px;margin-top:2px;padding-top:5px;border-top:1px solid ${TH.lineSoft}`, 'BLAST'));
      stat('Player/animal dmg', ex.dmg, (v) => { ex.dmg = v; });
      stat('Building dmg', ex.buildingDmg, (v) => { ex.buildingDmg = v; });
      stat('Radius', ex.radius, (v) => { ex.radius = Math.max(0.1, v); }, { hint: 'm' });
      if (ex.fuse != null) stat('Fuse', ex.fuse, (v) => { ex.fuse = Math.max(0, v); }, { hint: 'sec' });
    }
    card.replaceChildren(head, rows);
    return card;
  }

  // Explosive 5.56 etc: a per-ammo damage/speed multiplier + its own blast, layered on top of the base gun.
  private ammoModTable(): HTMLElement {
    const wrap = el('div', 'max-width:560px');
    const table = el('div', `border:1px solid ${TH.line};border-radius:${TH.r};overflow:hidden;background:${TH.panel}`);
    const hdr = el('div', `display:flex;align-items:center;gap:${TH.s3};padding:7px ${TH.s4};background:${TH.bg2};font:700 ${TH.f1} system-ui;letter-spacing:1px;color:${TH.dim}`);
    hdr.append(el('div', 'flex:1', 'AMMO'), el('div', 'width:60px;text-align:right', 'DMG×'), el('div', 'width:60px;text-align:right', 'SPD×'), el('div', 'width:60px;text-align:right', 'BLAST'));
    table.append(hdr);
    for (const [aid, mods] of Object.entries(AMMO_STATS)) {
      if (!mods) continue;
      const row = el('div', `display:flex;align-items:center;gap:${TH.s3};padding:8px ${TH.s4};border-top:1px solid ${TH.lineSoft}`);
      row.append(el('div', `flex:1;color:${TH.text};font:${TH.f3} system-ui`, AMMO_META[aid as keyof typeof AMMO_META]?.name ?? aid),
        tNum(mods.dmgMult ?? 1, (v) => { mods.dmgMult = v; this.markDirty(); }, { min: 0, w: 52 }),
        tNum(mods.speedMult ?? 1, (v) => { mods.speedMult = v; this.markDirty(); }, { min: 0, w: 52 }),
        mods.explosive
          ? tNum(mods.explosive.buildingDmg, (v) => { mods.explosive!.buildingDmg = v; this.markDirty(); }, { min: 0, w: 52, title: 'building dmg' })
          : el('div', `width:52px;text-align:right;color:${TH.faint}`, '—'));
      table.append(row);
    }
    wrap.append(table);
    return wrap;
  }

  // a persistent "+ Add item" slot: click opens a searchable item picker (the unified add path)
  private addSlot(onAdd: (id: string) => void): HTMLElement {
    const slot = el('div', `display:flex;align-items:center;justify-content:center;gap:6px;padding:8px;border:1px dashed ${TH.line};border-radius:${TH.rsm};color:${TH.faint};font:600 ${TH.f2} system-ui;cursor:pointer;margin-top:${TH.s2}`, '＋ Add item');
    slot.addEventListener('mouseenter', () => { slot.style.borderColor = TH.rust; slot.style.color = TH.rustHi; });
    slot.addEventListener('mouseleave', () => { slot.style.borderColor = TH.line; slot.style.color = TH.faint; });
    slot.addEventListener('click', () => openPicker(slot, onAdd));
    return slot;
  }

  // ---- research: a Default tab + 3 workbench-tier trees ----
  private renderResearch(): void {
    const bar = el('div', `display:flex;align-items:center;gap:${TH.s2};margin-bottom:${TH.s3};flex-wrap:wrap`);
    const tabs: Array<[number, HTMLElement]> = [0, 1, 2, 3].map((t) => [t, tBtn(t === 0 ? 'Default' : 'Lvl ' + t, () => { this.resTier = t; render(); })]);
    const paint = (): void => { for (const [t, b] of tabs) { const on = t === this.resTier; b.style.background = on ? TH.rust : TH.raised; b.style.color = on ? '#120b04' : TH.text; } };
    const clearBtn = tBtn('🗑 Clear tab', () => {
      const what = this.resTier === 0 ? 'all default items' : 'all Lvl ' + this.resTier + ' nodes';
      if (!window.confirm('Clear ' + what + '? (items return to the palette; their salvage costs are kept)')) return;
      if (this.resTier === 0) RESEARCH.defaults = [];
      else { const cleared = Object.keys(RESEARCH.nodes).filter((id) => RESEARCH.nodes[id].tier === this.resTier); for (const id of cleared) delete RESEARCH.nodes[id]; for (const m of Object.values(RESEARCH.nodes)) m.reqs = m.reqs.filter((q) => !cleared.includes(q)); }
      this.markDirty(); render();
    }, 'danger');
    bar.append(el('span', `font:700 ${TH.f1} system-ui;color:${TH.faint};letter-spacing:1px`, 'WORKBENCH'), ...tabs.map(([, b]) => b), spacer(), clearBtn);
    const host = el('div', 'height:calc(100% - 52px)');
    this.content.append(bar, host);
    const render = (): void => { paint(); this.refillPalette(); host.replaceChildren(); if (this.resTier === 0) this.renderDefaultGrid(host); else this.renderTree(host); };
    this.researchRefresh = render;
    render();
  }

  // Default tab: free-from-start items as a grid; drop/click adds, ✕ returns to the palette.
  private renderDefaultGrid(host: HTMLElement): void {
    host.append(el('div', `font:${TH.f3} system-ui;color:${TH.dim};margin-bottom:${TH.s3}`, 'Unlocked FREE from the start — no research. Click an item in the left palette (or drop below) to add it; these leave the palette.'));
    const grid = el('div', `display:grid;grid-template-columns:repeat(auto-fill,minmax(178px,1fr));gap:${TH.s2}`);
    for (const id of RESEARCH.defaults) {
      const card = el('div', `display:flex;align-items:center;gap:${TH.s2};background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.rsm};padding:7px 10px`);
      card.append(swatch(ITEMS[id as ItemId]?.color ?? 0x888888), el('div', `flex:1;font:${TH.f3} system-ui;color:${TH.text}`, ITEMS[id as ItemId]?.name ?? id),
        iconBtn('✕', () => { const i = RESEARCH.defaults.indexOf(id); if (i >= 0) RESEARCH.defaults.splice(i, 1); this.markDirty(); this.researchRefresh?.(); }));
      grid.append(card);
    }
    if (!RESEARCH.defaults.length) grid.append(el('div', `color:${TH.faint};font:${TH.f3} system-ui`, 'No default items.'));
    const drop = el('div', `margin-top:${TH.s3};padding:11px;border:1px dashed ${TH.line};border-radius:${TH.rsm};text-align:center;color:${TH.faint};font:600 ${TH.f2} system-ui`, '＋ drop a craftable here to make it default');
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.style.borderColor = TH.rust; });
    drop.addEventListener('dragleave', () => { drop.style.borderColor = TH.line; });
    drop.addEventListener('drop', (e) => { e.preventDefault(); drop.style.borderColor = TH.line; const id = e.dataTransfer?.getData('text/plain'); if (id && CRAFTABLE.has(id) && !RESEARCH.defaults.includes(id)) { RESEARCH.defaults.push(id); this.markDirty(); this.researchRefresh?.(); } });
    host.append(grid, drop);
  }

  // Tier tab: the pan/zoom snap-grid tree, showing nodes of tier <= resTier (cumulative).
  private renderTree(host: HTMLElement): void {
    const view = el('div', `position:relative;border:1px solid ${TH.line};border-radius:${TH.r};overflow:hidden;height:calc(100% - 44px);cursor:grab`);
    const world = el('div', `position:absolute;left:0;top:0;width:4000px;height:3000px;transform-origin:0 0;${gridBg(GRID)}`);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('width', '4000'); svg.setAttribute('height', '3000'); svg.style.cssText = 'position:absolute;inset:0;pointer-events:none';
    world.append(svg); view.append(world);

    const nodeEls = new Map<string, HTMLElement>();
    const vis = (id: string): boolean => RESEARCH.nodes[id].tier <= this.resTier;
    const redraw = (): void => {
      world.style.transform = `translate(${this.pan.x}px,${this.pan.y}px) scale(${this.zoom})`;
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      for (const id of Object.keys(RESEARCH.nodes)) {
        let box = nodeEls.get(id);
        if (!box) { box = this.makeResNode(id, redraw); nodeEls.set(id, box); world.append(box); }
        const n = RESEARCH.nodes[id];
        box.style.display = vis(id) ? 'block' : 'none';
        box.style.left = n.x + 'px'; box.style.top = n.y + 'px';
        box.style.borderColor = this.resSel.includes(id) ? TH.rust : TH.line;
        box.style.boxShadow = this.resSel.includes(id) ? `0 0 0 1px ${TH.rust}, 0 0 16px ${TH.glow}` : TH.soft;
      }
      for (const id of Object.keys(RESEARCH.nodes)) {
        if (!vis(id)) continue; const n = RESEARCH.nodes[id];
        for (const req of n.reqs) { const r = RESEARCH.nodes[req]; if (!r || !vis(req)) continue;
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', String(r.x + 150)); line.setAttribute('y1', String(r.y + 26));
          line.setAttribute('x2', String(n.x)); line.setAttribute('y2', String(n.y + 26));
          line.setAttribute('stroke', TH.rust); line.setAttribute('stroke-width', '2'); line.setAttribute('opacity', '0.8');
          svg.append(line);
        }
      }
    };
    const toolbar = el('div', `display:flex;align-items:center;gap:${TH.s2};margin-bottom:${TH.s3};flex-wrap:wrap`);
    toolbar.append(tBtn('🔗 Connect', () => this.resConnect(redraw), 'primary'), tBtn('✂ Disconnect', () => this.resDisconnect(redraw), 'danger'),
      tBtn('⊕ Fit', () => { this.pan = { x: 60, y: 60 }; this.zoom = 1; redraw(); }),
      el('span', `font:${TH.f1} system-ui;color:${TH.dim}`, 'drag or click craftables in · click 2 nodes + Connect · drag bg to pan · scroll to zoom'));
    host.append(toolbar, view);
    view.addEventListener('dragover', (e) => e.preventDefault());
    view.addEventListener('drop', (e) => {
      e.preventDefault(); const id = e.dataTransfer?.getData('text/plain'); if (!id || !CRAFTABLE.has(id) || RESEARCH.nodes[id]) return;
      const r = view.getBoundingClientRect();
      RESEARCH.nodes[id] = { reqs: [], x: snap((e.clientX - r.left - this.pan.x) / this.zoom), y: snap((e.clientY - r.top - this.pan.y) / this.zoom), tier: this.resTier }; // cost lives in RESEARCH.costs[id]
      this.markDirty(); redraw(); this.refillPalette();
    });
    view.addEventListener('mousedown', (e) => {
      if ((e.target as HTMLElement).closest('[data-node]')) return;
      e.preventDefault(); const sx = e.clientX, sy = e.clientY, px = this.pan.x, py = this.pan.y; view.style.cursor = 'grabbing';
      const mv = (ev: MouseEvent): void => { this.pan = { x: px + (ev.clientX - sx), y: py + (ev.clientY - sy) }; redraw(); };
      const up = (): void => { view.style.cursor = 'grab'; window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
      window.addEventListener('mousemove', mv); window.addEventListener('mouseup', up);
    });
    view.addEventListener('contextmenu', (e) => e.preventDefault());
    view.addEventListener('wheel', (e) => { e.preventDefault(); const f = e.deltaY < 0 ? 1.1 : 1 / 1.1; const nz = Math.max(0.35, Math.min(2, this.zoom * f)); const r = view.getBoundingClientRect(); const mx = e.clientX - r.left, my = e.clientY - r.top; this.pan = { x: mx - (mx - this.pan.x) * (nz / this.zoom), y: my - (my - this.pan.y) * (nz / this.zoom) }; this.zoom = nz; redraw(); }, { passive: false });
    redraw();
  }

  private makeResNode(id: string, redraw: () => void): HTMLElement {
    const n = RESEARCH.nodes[id];
    const box = el('div', `position:absolute;width:150px;background:linear-gradient(180deg,${TH.raised},${TH.panel});border:2px solid ${TH.line};border-radius:8px;padding:6px 8px;cursor:grab;box-shadow:${TH.soft}`);
    box.dataset.node = id;
    const top = el('div', `display:flex;align-items:center;gap:5px`);
    top.append(swatch(ITEMS[id as ItemId]?.color ?? 0x888888), el('span', `flex:1;font:700 ${TH.f2} system-ui;color:${TH.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`, ITEMS[id as ItemId]?.name ?? id));
    const tierBadge = el('div', `width:17px;height:17px;border-radius:4px;background:${TH.rust};color:#120b04;font:800 ${TH.f1} system-ui;display:flex;align-items:center;justify-content:center;cursor:pointer;flex:none`, String(n.tier));
    tierBadge.title = 'Click to cycle workbench tier'; tierBadge.addEventListener('click', (e) => { e.stopPropagation(); n.tier = (n.tier % 3) + 1; tierBadge.textContent = String(n.tier); this.markDirty(); redraw(); });
    top.append(tierBadge);
    const cost = el('div', 'display:flex;align-items:center;gap:5px;margin-top:5px');
    cost.append(el('span', `font:${TH.f1} system-ui;color:${TH.dim}`, 'salvage'), tNum(RESEARCH.costs[id] ?? DEFAULT_RESEARCH_COST, (v) => { RESEARCH.costs[id] = Math.max(0, Math.floor(v)); this.markDirty(); }, { min: 0, w: 56 }),
      iconBtn('✕', () => { delete RESEARCH.nodes[id]; for (const m of Object.values(RESEARCH.nodes)) m.reqs = m.reqs.filter((q) => q !== id); box.remove(); this.markDirty(); redraw(); this.refillPalette(); })); // cost stays in RESEARCH.costs -> shown in the palette after removal
    box.append(top, cost);
    box.addEventListener('click', (e) => { if ((e.target as HTMLElement).tagName === 'INPUT') return; this.resToggle(id); redraw(); });
    box.addEventListener('mousedown', (e) => {
      if ((e.target as HTMLElement).tagName === 'INPUT' || e.button !== 0) return;
      e.preventDefault(); e.stopPropagation();
      const sx = e.clientX, sy = e.clientY, x0 = n.x, y0 = n.y;
      const mv = (ev: MouseEvent): void => { n.x = Math.max(0, snap(x0 + (ev.clientX - sx) / this.zoom)); n.y = Math.max(0, snap(y0 + (ev.clientY - sy) / this.zoom)); redraw(); };
      const up = (): void => { this.markDirty(); window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
      window.addEventListener('mousemove', mv); window.addEventListener('mouseup', up);
    });
    return box;
  }

  private resToggle(id: string): void { const s = this.resSel; const i = s.indexOf(id); if (i >= 0) s.splice(i, 1); else { s.push(id); if (s.length > 2) s.shift(); } }
  private resConnect(redraw: () => void): void {
    const [a, b] = this.resSel; if (!a || !b || a === b) return;
    if (RESEARCH.nodes[b].reqs.includes(a)) return;
    if (this.resHasPath(a, b)) { this.toast('Would create a cycle'); return; }
    RESEARCH.nodes[b].reqs.push(a); this.resSel = []; this.markDirty(); redraw();
  }
  private resDisconnect(redraw: () => void): void {
    const [a, b] = this.resSel; if (!a || !b) return;
    const rm = (x: string, y: string): boolean => { const i = RESEARCH.nodes[y]?.reqs.indexOf(x) ?? -1; if (i >= 0) { RESEARCH.nodes[y].reqs.splice(i, 1); return true; } return false; };
    if (rm(a, b) || rm(b, a)) { this.resSel = []; this.markDirty(); redraw(); }
  }
  private resHasPath(start: string, target: string): boolean {
    const seen = new Set<string>(); const stack = [start];
    while (stack.length) { const cur = stack.pop()!; if (cur === target) return true; if (seen.has(cur)) continue; seen.add(cur); for (const r of RESEARCH.nodes[cur]?.reqs ?? []) stack.push(r); }
    return false;
  }

  private toast(msg: string): void {
    const t = el('div', `position:fixed;left:50%;bottom:26px;transform:translateX(-50%);background:${TH.panel};border:1px solid ${TH.rustLo};color:${TH.rustHi};padding:9px 17px;border-radius:${TH.r};z-index:201;box-shadow:0 8px 30px rgba(0,0,0,0.6)`, msg);
    document.body.appendChild(t); window.clearTimeout(this.toastT); this.toastT = window.setTimeout(() => t.remove(), 1700);
  }

  show(): void { this.open = true; this.paintNav(); this.refillPalette(); this.renderMain(); this.root.style.display = 'block'; this.onVisibility?.(true); }
  hide(): void { if (this.dirty && !window.confirm('Unsaved changes — close anyway?')) return; this.open = false; this.root.style.display = 'none'; this.onVisibility?.(false); }
  toggle(): void { this.open ? this.hide() : this.show(); }
}

// ---- shared builders ----
function spacer(): HTMLElement { return el('div', 'flex:1'); }
function right(t: string): HTMLElement { return el('span', 'text-align:right', t); }
function swatch(color: number): HTMLElement { return el('span', `width:12px;height:12px;border-radius:3px;flex:none;background:#${color.toString(16).padStart(6, '0')};box-shadow:${TH.inset}`); }
function heading(title: string, sub: string): HTMLElement { const d = el('div', `margin-bottom:${TH.s4}`); d.append(el('div', `font:700 ${TH.f5} system-ui;color:${TH.text}`, title), el('div', `font:${TH.f3} system-ui;color:${TH.dim};margin-top:3px`, sub)); return d; }
function cardGrid(min = 200): HTMLElement { return el('div', `display:grid;grid-template-columns:repeat(auto-fill,minmax(${min}px,1fr));gap:${TH.s3}`); }
const cardCss = `background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.r};padding:${TH.s3} ${TH.s4};box-shadow:${TH.soft}`;
function iconBtn(label: string, onClick: () => void): HTMLElement { const b = el('div', `width:20px;height:20px;border-radius:4px;background:${TH.sunk};border:1px solid ${TH.line};color:${TH.dim};display:flex;align-items:center;justify-content:center;cursor:pointer;font:${TH.f3} system-ui;flex:none`, label); b.addEventListener('click', onClick); return b; }
function prettify(id: string): string { return id.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()); }
function sectionLabel(t: string): HTMLElement { return el('div', `font:700 ${TH.f1} system-ui;color:${TH.dim};letter-spacing:1.5px;margin:${TH.s5} 0 ${TH.s2};padding-bottom:${TH.s1};border-bottom:1px solid ${TH.lineSoft}`, t.toUpperCase()); }
function recipeCat(id: string): string {
  if (id.includes('axe') || id.includes('pickaxe')) return 'Gather tools';
  if (['rock', 'torch', 'hammer', 'blueprint'].includes(id)) return 'Tools';
  if (id.includes('door') || id.includes('window')) return 'Doors & windows';
  if (id.includes('trap') || id.includes('mine') || id.includes('spikes')) return 'Traps';
  if (['chest', 'bigchest', 'campfire', 'locker', 'fridge', 'drop_box'].includes(id)) return 'Storage';
  return 'Deployables';
}
function assetCat(id: string): string {
  if (id.startsWith('barrel')) return 'Barrels';
  if (id.startsWith('crate')) return 'Crates';
  if (id.startsWith('toolbox')) return 'Toolboxes';
  return 'Junk piles';
}
function weaponCat(id: WeaponId): string {
  const kind = WEAPONS[id].kind;
  if (kind === 'melee') return 'Melee';
  if (kind === 'bow') return 'Bows';
  if (kind === 'gun') return 'Guns';
  return 'Throwables & explosives'; // throw / place
}

// floating searchable item picker (the click-to-add path; drag still works independently)
let pickerEl: HTMLElement | null = null;
function closePicker(): void { if (pickerEl) { pickerEl.remove(); pickerEl = null; document.removeEventListener('mousedown', onDocDown); } }
function onDocDown(e: MouseEvent): void { if (pickerEl && !pickerEl.contains(e.target as Node)) closePicker(); }
function openPicker(anchor: HTMLElement, onPick: (id: string) => void): void {
  closePicker();
  const r = anchor.getBoundingClientRect();
  const pop = el('div', `position:fixed;z-index:300;width:244px;max-height:330px;display:flex;flex-direction:column;background:${TH.panel};border:1px solid ${TH.line};border-radius:${TH.r};box-shadow:0 14px 44px rgba(0,0,0,0.7);overflow:hidden`);
  pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - 254)) + 'px';
  pop.style.top = Math.min(r.bottom + 6, window.innerHeight - 340) + 'px';
  const search = el('input', `margin:${TH.s2};padding:7px 9px;background:${TH.sunk};border:1px solid ${TH.line};border-radius:${TH.rsm};color:${TH.text};font:${TH.f3} system-ui`);
  search.placeholder = 'Search items…';
  const list = el('div', `flex:1;overflow:auto;padding:0 ${TH.s2} ${TH.s2}`);
  const fill = (): void => {
    list.replaceChildren();
    const q = search.value.trim().toLowerCase();
    for (const [id, def] of Object.entries(ITEMS)) {
      if (q && !def.name.toLowerCase().includes(q) && !id.toLowerCase().includes(q)) continue;
      const row = el('div', `display:flex;align-items:center;gap:8px;padding:6px 7px;border-radius:${TH.rsm};cursor:pointer`);
      row.append(swatch(def.color), el('span', `flex:1;color:${TH.text};font:${TH.f3} system-ui`, def.name));
      row.addEventListener('mouseenter', () => { row.style.background = TH.hover; });
      row.addEventListener('mouseleave', () => { row.style.background = 'transparent'; });
      row.addEventListener('click', () => { onPick(id); closePicker(); });
      list.append(row);
    }
  };
  search.addEventListener('input', fill); fill();
  pop.append(search, list);
  document.body.appendChild(pop); pickerEl = pop;
  search.focus();
  setTimeout(() => document.addEventListener('mousedown', onDocDown), 0);
}
